import { useState } from "react";
import { postKmsData, translate, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { BulkActionProps } from "@kaltura/mediaspace-shared-selectable-media-list";

export interface BulkRestoreActionProps extends BulkActionProps {
    apiUrl: string;
}

/**
 * Hook for handling bulk restore actions.
 * 1. Opens a confirmation modal.
 * 2. Sends a request to the backend to restore the selected entries.
 * 3. Reloads the media list after successful restore.
 * 4. Closes the confirmation modal.
 * 5. Refetches the media list.
 *
 * @param entryIds - The IDs of the entries to be restored.
 * @param reloadMedia - A function to reload the media list.
 * @param apiUrl - The URL of the API endpoint for restoring entries.
 * @returns An object containing the selected count, open confirmation state, processing state, handle click function, handle restore function, and close confirmation function.
 */
export const useBulkRestore = ({ entryIds, reloadMedia, apiUrl }: BulkRestoreActionProps) => {
    const selectedCount = entryIds.length;
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [processing, setProcessing] = useState(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const closeConfirmation = () => {
        setOpenConfirmation(false);
    };

    const handleClick = () => {
        setOpenConfirmation(true);
        sendButtonAnalytics("Bulk restore items", ButtonClickAnalyticsType.EDIT);
    };

    const handleRestore = async () => {
        setProcessing(true);
        try {
            const response = await postKmsData(apiUrl, { ids: entryIds });
            if (response.success) {
                showToast({
                    severity: "success",
                    message: translatePlural(
                        "%1 item has been restored.",
                        "%1 items have been restored.",
                        [selectedCount]
                    ),
                });
                closeConfirmation();
                reloadMedia?.();
            }
            else {
                showToast({
                    severity: "error",
                    message: translate("Failed to restore items."),
                });
            }
        }
        catch (e: unknown) {
            console.error("Failed to restore entries.", e);
            showToast({
                severity: "error",
                message: translate("Failed to restore items."),
            });
        }
        finally {
            setProcessing(false);
        }
    };

    return { selectedCount, openConfirmation, processing, handleClick, handleRestore, closeConfirmation };
};
